import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { CtaButton } from './components/CtaButton';

interface CaptureNote {
  icon: ReactNode;
  // Literal label (e.g. a timestamp) or an i18n key — exactly one is set.
  label?: string;
  labelKey?: string;
  textKey: string;
  isQuote?: boolean;
  offset: string;
  // Staggered float so the cards drift gently out of sync (see HTML .fl/.fl.s).
  float: string;
}

const NOTES: CaptureNote[] = [
  {
    icon: (
      <svg viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v4l3 2" />
      </svg>
    ),
    label: '2:14 AM',
    textKey: 'note1Text',
    offset: 'lg:mr-[46px]',
    float: 'animate-floaty',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24">
        <path d="M13 5l7 7-7 7M20 12H4" />
      </svg>
    ),
    labelKey: 'note2Label',
    textKey: 'note2Text',
    isQuote: true,
    offset: 'lg:ml-[38px]',
    float: 'animate-floaty-slow',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24">
        <rect x="3" y="4" width="18" height="17" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </svg>
    ),
    labelKey: 'note3Label',
    textKey: 'note3Text',
    offset: 'lg:mr-[22px]',
    float: 'animate-floaty-delayed',
  },
];

const CheckIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

export const Section14 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.card}>
          {/* Light-theme card decorations: masked dot grid + pastel glows. */}
          <div className={styles.cardGrid} aria-hidden="true" />
          <div className={styles.cardGlow} aria-hidden="true" />
          <div className={styles.text.container}>
            <h2 className={styles.text.title}>
              {t.rich('pages.home.section14.title', {
                highlight: (chunks) => (
                  <span className={styles.emphasis}>{chunks}</span>
                ),
              })}
            </h2>
            <p className={styles.text.description}>
              {t('pages.home.section14.description')}
            </p>
            <CtaButton
              variant="coral"
              href={appRoutes.LOGIN}
              reportLocation="Homepage Band"
            >
              {t('pages.home.section14.cta')}
            </CtaButton>
          </div>

          <div className={styles.notes.container}>
            {NOTES.map((note) => (
              <div
                key={note.textKey}
                className={classNames(
                  styles.notes.card,
                  note.offset,
                  note.float,
                )}
              >
                <div className={styles.notes.top}>
                  <span className={styles.notes.label}>
                    {note.icon}
                    {note.labelKey
                      ? t(`pages.home.section14.${note.labelKey}`)
                      : note.label}
                  </span>
                  <span className={styles.notes.tag}>
                    <CheckIcon />
                    {t('pages.home.section14.captured')}
                  </span>
                </div>
                <p
                  className={classNames(
                    styles.notes.text,
                    note.isQuote && styles.notes.quote,
                  )}
                >
                  {t(`pages.home.section14.${note.textKey}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy py-[60px]',
  wrap: 'max-w-[1200px] mx-auto px-6 lg:px-10',
  card: classNames(
    'relative grid grid-cols-1 items-center gap-12 overflow-hidden rounded-[28px] border',
    'border-[#F0F0F0] bg-white',
    'marketing-dark:border-white/10',
    'marketing-dark:bg-[radial-gradient(120%_140%_at_88%_12%,rgba(45,86,210,0.42)_0%,rgba(28,58,150,0.12)_38%,transparent_62%),linear-gradient(160deg,#0e1f52_0%,#0a1838_70%)]',
    'px-[34px] py-11 text-center',
    'lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 lg:px-[60px] lg:py-[60px] lg:text-left',
  ),
  // Light-only: dot grid fading in from the top-right corner of the card.
  cardGrid: classNames(
    'pointer-events-none absolute inset-0 marketing-dark:hidden',
    'bg-[radial-gradient(circle,#DADADA_1px,transparent_1.4px)] bg-[length:28px_28px]',
    '[mask-image:radial-gradient(70%_90%_at_88%_10%,#000_0%,transparent_70%)]',
  ),
  // Light-only: pastel-blue glows in opposite corners.
  cardGlow: classNames(
    'pointer-events-none absolute inset-0 marketing-dark:hidden',
    'bg-[radial-gradient(46%_60%_at_92%_6%,rgba(189,222,255,0.45)_0%,transparent_62%),radial-gradient(36%_50%_at_4%_96%,rgba(189,222,255,0.32)_0%,transparent_60%)]',
  ),
  text: {
    container: 'relative z-[2] mx-auto lg:mx-0',
    title:
      'max-w-[520px] text-[32px] font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[38px]',
    description:
      'mx-auto my-4 mb-7 max-w-[480px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/70 lg:mx-0',
  },
  emphasis: 'text-mf-blue marketing-dark:text-mf-coral',
  notes: {
    container:
      'relative z-[2] mx-auto flex w-full max-w-[400px] flex-col gap-[14px] lg:ml-auto',
    card: classNames(
      'rounded-2xl border px-[17px] py-[15px] text-left',
      'border-[#F0F0F0] bg-white shadow-[0_8px_22px_rgba(20,30,60,0.06)]',
      'marketing-dark:border-white/[0.13] marketing-dark:bg-white/[0.055]',
      'marketing-dark:shadow-[0_18px_44px_rgba(2,8,30,0.4)] marketing-dark:backdrop-blur-[10px]',
      'motion-reduce:animate-none',
    ),
    top: 'mb-[9px] flex items-center gap-[9px]',
    label: classNames(
      'inline-flex items-center gap-1.5 text-xs font-bold',
      'text-mf-blue marketing-dark:text-mf-coral',
      '[&>svg]:h-[13px] [&>svg]:w-[13px] [&>svg]:fill-none [&>svg]:stroke-2',
      '[&>svg]:stroke-mf-blue marketing-dark:[&>svg]:stroke-mf-coral',
    ),
    tag: classNames(
      'ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold',
      'text-mf-gray-500 marketing-dark:text-white/50',
      '[&>svg]:h-3 [&>svg]:w-3 [&>svg]:fill-none [&>svg]:stroke-[2.4]',
      '[&>svg]:stroke-[#2BA471] marketing-dark:[&>svg]:stroke-[#5fd39a]',
    ),
    text: 'text-[15px] leading-[1.5] text-mf-off-black-600 marketing-dark:text-white',
    quote: 'font-serif text-base italic leading-[1.45]',
  },
};
